import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { BarChart3, Users, FileText, DollarSign, Download, TrendingUp, CheckCircle, XCircle, Plus, Trash2, Edit2, Save, Tag, Columns, Settings, UserCheck, MessageCircle, Send, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { adminAPI, appealsAPI, plansAPI, suggestionsAPI, platformFieldsAPI, settingsAPI } from '../lib/api';
import { toast } from 'sonner';
import { useTheme } from '../App';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const STATUS_MAP = {
  pending_payment: { dark: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', light: 'bg-yellow-50 text-yellow-600 border-yellow-200' },
  submitted: { dark: 'bg-blue-500/10 text-blue-400 border-blue-500/20', light: 'bg-blue-50 text-blue-600 border-blue-200' },
  in_review: { dark: 'bg-purple-500/10 text-purple-400 border-purple-500/20', light: 'bg-purple-50 text-purple-600 border-purple-200' },
  approved: { dark: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', light: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
  rejected: { dark: 'bg-red-500/10 text-red-400 border-red-500/20', light: 'bg-red-50 text-red-600 border-red-200' },
  completed: { dark: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20', light: 'bg-cyan-50 text-cyan-600 border-cyan-200' },
};

const PIE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

export default function AdminDashboard() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [stats, setStats] = useState(null);
  const [appeals, setAppeals] = useState([]);
  const [plans, setPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [platformFields, setPlatformFields] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingPlan, setEditingPlan] = useState(null);
  const [newPlan, setNewPlan] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [affiliateStats, setAffiliateStats] = useState(null);
  const [sysSettings, setSysSettings] = useState(null);
  const [sendingAuthority, setSendingAuthority] = useState({});

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [statsRes, appealsRes, plansRes, usersRes, suggestionsRes, fieldsRes] = await Promise.all([
        adminAPI.stats(), appealsAPI.list({ limit: 50 }), plansAPI.list(false), adminAPI.users({ limit: 50 }),
        suggestionsAPI.list(false), platformFieldsAPI.list()
      ]);
      setStats(statsRes.data);
      setAppeals(appealsRes.data.appeals);
      setPlans(plansRes.data);
      setUsers(usersRes.data.users);
      setSuggestions(suggestionsRes.data);
      setPlatformFields(fieldsRes.data);
    } catch { toast.error('Failed to load data'); }
  };

  const loadContacts = async () => {
    try { const res = await settingsAPI.contacts(); setContacts(res.data); } catch { toast.error('Failed to load contacts'); }
  };

  const loadAffiliates = async () => {
    try { const res = await settingsAPI.affiliates(); setAffiliates(res.data.affiliates); setAffiliateStats(res.data); } catch { toast.error('Failed to load affiliates'); }
  };

  const loadSettings = async () => {
    try { const res = await settingsAPI.get(); setSysSettings(res.data); } catch { toast.error('Failed to load settings'); }
  };

  const handleDeleteContact = async (id) => {
    try { await settingsAPI.deleteContact(id); toast.success('Deleted'); loadContacts(); } catch { toast.error('Failed'); }
  };

  const handlePayout = async (userId) => {
    try { await settingsAPI.payout(userId); toast.success('Payout marked'); loadAffiliates(); } catch { toast.error('Failed'); }
  };

  const handleSaveSettings = async () => {
    if (!sysSettings) return;
    try { await settingsAPI.update(sysSettings); toast.success('Settings saved'); } catch { toast.error('Failed to save'); }
  };

  const handleSendToAuthority = async (appealId) => {
    setSendingAuthority(prev => ({...prev, [appealId]: true}));
    try {
      await settingsAPI.sendToAuthority(appealId);
      toast.success('Sent to authority');
      loadData();
    } catch { toast.error('Failed to send'); }
    finally { setSendingAuthority(prev => ({...prev, [appealId]: false})); }
  };

  const handleStatusChange = async (id, s) => { try { await appealsAPI.updateStatus(id, s); toast.success('Updated'); loadData(); } catch { toast.error('Failed'); } };
  const handleDeleteAppeal = async (id) => { try { await appealsAPI.delete(id); toast.success('Deleted'); loadData(); } catch { toast.error('Failed'); } };
  const handleExport = async (format) => {
    try {
      const res = await adminAPI.exportAppeals(format);
      if (format === 'excel' || format === 'csv') {
        const blob = new Blob([res.data], { type: format === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'text/csv' });
        const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `appeals.${format === 'excel' ? 'xlsx' : 'csv'}`; a.click();
      }
      toast.success('Export complete');
    } catch { toast.error('Export failed'); }
  };
  const handleSavePlan = async (plan) => { try { plan.id ? await plansAPI.update(plan.id, plan) : await plansAPI.create(plan); toast.success('Saved'); setEditingPlan(null); setNewPlan(null); loadData(); } catch { toast.error('Failed'); } };
  const handleDeletePlan = async (id) => { try { await plansAPI.delete(id); toast.success('Deleted'); loadData(); } catch { toast.error('Failed'); } };

  const filteredAppeals = statusFilter === 'all' ? appeals : appeals.filter(a => a.status === statusFilter);
  const getStatus = (s) => STATUS_MAP[s]?.[isDark ? 'dark' : 'light'] || '';

  const card = isDark ? 'bg-[#0A0A0C]/60 backdrop-blur-xl border border-white/[0.05]' : 'bg-white border border-gray-200 shadow-sm';
  const textP = isDark ? 'text-white' : 'text-gray-900';
  const textS = isDark ? 'text-white/60' : 'text-gray-500';
  const textM = isDark ? 'text-white/40' : 'text-gray-400';
  const inputCl = isDark ? 'bg-white/[0.03] border-white/[0.06] text-white' : 'bg-gray-50 border-gray-200 text-gray-900';
  const dropCl = isDark ? 'bg-[#0A0A0C] border-white/10' : 'bg-white border-gray-200';
  const itemCl = isDark ? 'text-white hover:bg-white/5' : 'text-gray-700 hover:bg-gray-50';
  const rowHover = isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-gray-50';
  const borderCl = isDark ? 'border-white/[0.05]' : 'border-gray-100';

  const statCards = stats ? [
    { icon: FileText, label: t('admin.totalAppeals'), value: stats.total_appeals, color: 'text-blue-500', bg: isDark ? 'bg-blue-500/10' : 'bg-blue-50' },
    { icon: DollarSign, label: t('admin.revenue'), value: `€${stats.total_revenue.toFixed(2)}`, color: 'text-emerald-500', bg: isDark ? 'bg-emerald-500/10' : 'bg-emerald-50' },
    { icon: Users, label: t('admin.users'), value: stats.total_users, color: 'text-purple-500', bg: isDark ? 'bg-purple-500/10' : 'bg-purple-50' },
    { icon: TrendingUp, label: t('admin.subscribers'), value: stats.total_subscribers, color: 'text-cyan-500', bg: isDark ? 'bg-cyan-500/10' : 'bg-cyan-50' },
    { icon: CheckCircle, label: t('admin.approved'), value: stats.approved, color: 'text-emerald-500', bg: isDark ? 'bg-emerald-500/10' : 'bg-emerald-50' },
    { icon: XCircle, label: t('admin.rejected'), value: stats.rejected, color: 'text-red-500', bg: isDark ? 'bg-red-500/10' : 'bg-red-50' },
  ] : [];

  return (
    <div className={`min-h-screen py-8 px-6 md:px-12 transition-colors ${isDark ? '' : 'bg-[#F8FAFC]'}`} data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center justify-between mb-8">
            <h1 className={`text-3xl font-bold tracking-tight ${textP}`} style={{ fontFamily: 'Outfit' }}>{t('admin.title')}</h1>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => handleExport('csv')} className={`${isDark ? 'border-white/[0.08] text-white/60' : 'border-gray-200 text-gray-500'}`} data-testid="export-csv-btn"><Download className="w-4 h-4 mr-2" /> CSV</Button>
              <Button variant="outline" onClick={() => handleExport('excel')} className={`${isDark ? 'border-white/[0.08] text-white/60' : 'border-gray-200 text-gray-500'}`} data-testid="export-excel-btn"><Download className="w-4 h-4 mr-2" /> Excel</Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {statCards.map((c, i) => (
              <motion.div key={c.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className={`${card} rounded-2xl p-4`}>
                <div className={`w-8 h-8 ${c.bg} rounded-lg flex items-center justify-center mb-3`}><c.icon className={`w-4 h-4 ${c.color}`} /></div>
                <div className={`text-2xl font-bold ${textP}`} style={{ fontFamily: 'Outfit' }}>{c.value}</div>
                <div className={`text-xs mt-1 ${textM}`}>{c.label}</div>
              </motion.div>
            ))}
          </div>

          {/* Charts */}
          {stats && stats.platform_distribution.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div className={`${card} rounded-2xl p-6`}>
                <h3 className={`text-sm font-medium ${textS} mb-4`}>Platform Distribution</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={stats.platform_distribution}>
                    <XAxis dataKey="platform" tick={{ fill: isDark ? '#94A3B8' : '#6B7280', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: isDark ? '#94A3B8' : '#6B7280', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ backgroundColor: isDark ? '#0A0A0C' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`, borderRadius: '12px', color: isDark ? '#fff' : '#111' }} />
                    <Bar dataKey="count" fill="#3B82F6" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className={`${card} rounded-2xl p-6`}>
                <h3 className={`text-sm font-medium ${textS} mb-4`}>Status Overview</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={[{ name: 'Approved', value: stats.approved },{ name: 'Rejected', value: stats.rejected },{ name: 'In Review', value: stats.in_review },{ name: 'Pending', value: stats.pending }].filter(d => d.value > 0)} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                      {[0,1,2,3].map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: isDark ? '#0A0A0C' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`, borderRadius: '12px', color: isDark ? '#fff' : '#111' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Tabs */}
          <Tabs defaultValue="appeals" className="space-y-4">
            <TabsList className={`${isDark ? 'bg-white/[0.03] border border-white/[0.06]' : 'bg-gray-100 border border-gray-200'} rounded-xl p-1 flex-wrap gap-1`}>
              <TabsTrigger value="appeals" className={`data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-500 rounded-lg`} data-testid="tab-appeals">Appeals</TabsTrigger>
              <TabsTrigger value="plans" className={`data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-500 rounded-lg`} data-testid="tab-plans">{t('admin.managePlans')}</TabsTrigger>
              <TabsTrigger value="suggestions" className={`data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-500 rounded-lg`} data-testid="tab-suggestions">Suggestions</TabsTrigger>
              <TabsTrigger value="fields" className={`data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-500 rounded-lg`} data-testid="tab-fields">Form Fields</TabsTrigger>
              <TabsTrigger value="users" className={`data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-500 rounded-lg`} data-testid="tab-users">{t('admin.manageUsers')}</TabsTrigger>
              <TabsTrigger value="affiliates" className={`data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-500 rounded-lg`} data-testid="tab-affiliates" onClick={loadAffiliates}>Affiliates</TabsTrigger>
              <TabsTrigger value="contacts" className={`data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-500 rounded-lg`} data-testid="tab-contacts" onClick={loadContacts}>Contacts</TabsTrigger>
              <TabsTrigger value="settings" className={`data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-500 rounded-lg`} data-testid="tab-settings" onClick={loadSettings}>Settings</TabsTrigger>
            </TabsList>

            {/* Appeals Tab */}
            <TabsContent value="appeals">
              <div className={`${card} rounded-2xl overflow-hidden`}>
                <div className={`p-4 border-b ${borderCl} flex items-center gap-3`}>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className={`w-48 ${inputCl} rounded-lg`} data-testid="status-filter"><SelectValue /></SelectTrigger>
                    <SelectContent className={dropCl}>
                      {['all','pending_payment','submitted','in_review','approved','rejected'].map(s => (
                        <SelectItem key={s} value={s} className={itemCl}>{s === 'all' ? t('common.all') : s.replace('_',' ')}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead><tr className={`border-b ${borderCl}`}>
                      {['ID','Platform','Issue','Name','Status','Payment','Date','Authority','Actions'].map(h => (
                        <th key={h} className={`text-left text-xs font-medium p-4 ${textM}`}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {filteredAppeals.map(a => (
                        <tr key={a.id} className={`border-b ${borderCl} ${rowHover} transition-colors`} data-testid={`appeal-row-${a.id}`}>
                          <td className={`p-4 text-xs font-mono ${textS}`}>{a.tracking_code}</td>
                          <td className={`p-4 text-sm ${textP}`}>{a.platform}</td>
                          <td className={`p-4 text-sm ${textS}`}>{a.issue_type}</td>
                          <td className={`p-4 text-sm ${textP}`}>{a.full_name}</td>
                          <td className="p-4">
                            <Select value={a.status} onValueChange={(v) => handleStatusChange(a.id, v)}>
                              <SelectTrigger className={`h-7 text-xs border rounded-full px-3 w-32 ${getStatus(a.status)}`}><SelectValue /></SelectTrigger>
                              <SelectContent className={dropCl}>
                                {['pending_payment','submitted','in_review','approved','rejected','completed'].map(s => (
                                  <SelectItem key={s} value={s} className={`${itemCl} text-xs`}>{s.replace('_',' ')}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-4"><span className={`text-xs px-2 py-1 rounded-full ${a.payment_status === 'paid' ? (isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600') : (isDark ? 'bg-yellow-500/10 text-yellow-400' : 'bg-yellow-50 text-yellow-600')}`}>{a.payment_status}</span></td>
                          <td className={`p-4 text-xs ${textM}`}>{new Date(a.created_at).toLocaleDateString()}</td>
                          <td className="p-4">
                            {a.sent_to_authority ? (
                              <span className={`flex items-center gap-1 text-xs ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}><CheckCircle className="w-3.5 h-3.5" /> Sent</span>
                            ) : (
                              <Button variant="ghost" size="sm" onClick={() => handleSendToAuthority(a.id)} disabled={sendingAuthority[a.id]} className={`text-xs h-7 px-2 ${isDark ? 'text-blue-400 hover:text-blue-300 hover:bg-blue-500/10' : 'text-blue-600 hover:bg-blue-50'}`} data-testid={`send-authority-${a.id}`}>
                                <Send className="w-3 h-3 mr-1" /> Send
                              </Button>
                            )}
                          </td>
                          <td className="p-4"><Button variant="ghost" size="sm" onClick={() => handleDeleteAppeal(a.id)} className="text-red-500 hover:text-red-400 h-7 w-7 p-0" data-testid={`delete-appeal-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredAppeals.length === 0 && <div className={`p-12 text-center ${textM}`}>{t('common.noResults')}</div>}
                </div>
              </div>
            </TabsContent>

            {/* Plans Tab */}
            <TabsContent value="plans">
              <div className={`${card} rounded-2xl p-6`}>
                <div className="flex justify-between items-center mb-6">
                  <h3 className={`text-lg font-semibold ${textP}`} style={{ fontFamily: 'Outfit' }}>{t('admin.managePlans')}</h3>
                  <Button onClick={() => setNewPlan({ name: '', description: '', price: 0, currency: 'eur', billing_cycle: 'monthly', max_appeals: 1, features: [], active: true, sort_order: 0 })} className="bg-blue-500 hover:bg-blue-600 rounded-xl text-white" data-testid="add-plan-btn"><Plus className="w-4 h-4 mr-2" /> Add Plan</Button>
                </div>
                <div className="space-y-4">
                  {plans.map(plan => (
                    <div key={plan.id} className={`flex items-center justify-between p-4 rounded-xl border ${isDark ? 'bg-white/[0.02] border-white/[0.05]' : 'bg-gray-50 border-gray-200'}`} data-testid={`plan-card-${plan.id}`}>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className={`font-semibold ${textP}`}>{plan.name}</span>
                          <Badge className={plan.active ? (isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600') : (isDark ? 'bg-red-500/10 text-red-400' : 'bg-red-50 text-red-600')}>{plan.active ? 'Active' : 'Inactive'}</Badge>
                        </div>
                        <p className={`text-sm mt-1 ${textS}`}>{plan.description}</p>
                        <p className={`text-sm mt-1 ${textM}`}>{plan.currency === 'eur' ? '€' : '$'}{plan.price} / {plan.billing_cycle} | Max: {plan.max_appeals === -1 ? 'Unlimited' : plan.max_appeals}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setEditingPlan({...plan})} className="text-blue-500"><Edit2 className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeletePlan(plan.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
                {(editingPlan || newPlan) && <PlanEditor plan={editingPlan || newPlan} onSave={handleSavePlan} onCancel={() => { setEditingPlan(null); setNewPlan(null); }} isDark={isDark} />}
              </div>
            </TabsContent>

            {/* Suggestions Tab */}
            <TabsContent value="suggestions">
              <SuggestionsManager suggestions={suggestions} onReload={loadData} isDark={isDark} card={card} textP={textP} textS={textS} textM={textM} inputCl={inputCl} />
            </TabsContent>

            {/* Form Fields Tab */}
            <TabsContent value="fields">
              <FieldsManager fields={platformFields} onReload={loadData} isDark={isDark} card={card} textP={textP} textS={textS} textM={textM} inputCl={inputCl} dropCl={dropCl} itemCl={itemCl} />
            </TabsContent>

            {/* Users Tab */}
            <TabsContent value="users">
              <div className={`${card} rounded-2xl overflow-hidden`}>
                <table className="w-full">
                  <thead><tr className={`border-b ${borderCl}`}>
                    {['Name','Email','Role','Joined'].map(h => (<th key={h} className={`text-left text-xs font-medium p-4 ${textM}`}>{h}</th>))}
                  </tr></thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id} className={`border-b ${borderCl} ${rowHover}`} data-testid={`user-row-${u.id}`}>
                        <td className={`p-4 text-sm ${textP}`}>{u.name}</td>
                        <td className={`p-4 text-sm ${textS}`}>{u.email}</td>
                        <td className="p-4">
                          <Select value={u.role} onValueChange={async (v) => { try { await adminAPI.updateRole(u.id, v); toast.success('Updated'); loadData(); } catch { toast.error('Failed'); } }}>
                            <SelectTrigger className={`h-7 text-xs w-32 ${inputCl} rounded-lg`}><SelectValue /></SelectTrigger>
                            <SelectContent className={dropCl}>
                              {['subscriber','authority','super_admin','affiliate'].map(r => (<SelectItem key={r} value={r} className={itemCl}>{r.replace('_',' ')}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className={`p-4 text-xs ${textM}`}>{new Date(u.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            {/* Affiliates Tab */}
            <TabsContent value="affiliates">
              <div className={`${card} rounded-2xl p-6`}>
                <div className="flex items-center gap-2 mb-4">
                  <UserCheck className="w-5 h-5 text-blue-500" />
                  <h3 className={`text-lg font-semibold ${textP}`} style={{ fontFamily: 'Outfit' }}>Affiliate Partners</h3>
                </div>
                {affiliateStats && (
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className={`p-4 rounded-xl ${isDark ? 'bg-white/[0.03] border border-white/[0.05]' : 'bg-gray-50 border border-gray-100'}`}>
                      <div className={`text-xs ${textM} mb-1`}>Commission Rate</div>
                      <div className={`text-xl font-bold ${textP}`} style={{ fontFamily: 'Outfit' }}>${affiliateStats.commission_per_appeal}/appeal</div>
                    </div>
                    <div className={`p-4 rounded-xl ${isDark ? 'bg-white/[0.03] border border-white/[0.05]' : 'bg-gray-50 border border-gray-100'}`}>
                      <div className={`text-xs ${textM} mb-1`}>Total Pending</div>
                      <div className={`text-xl font-bold text-amber-500`} style={{ fontFamily: 'Outfit' }}>${(affiliateStats.total_pending || 0).toFixed(2)}</div>
                    </div>
                    <div className={`p-4 rounded-xl ${isDark ? 'bg-white/[0.03] border border-white/[0.05]' : 'bg-gray-50 border border-gray-100'}`}>
                      <div className={`text-xs ${textM} mb-1`}>Total Paid Out</div>
                      <div className={`text-xl font-bold text-emerald-500`} style={{ fontFamily: 'Outfit' }}>${(affiliateStats.total_paid || 0).toFixed(2)}</div>
                    </div>
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead><tr className={`border-b ${borderCl}`}>
                      {['Name','Email','Ref Code','Referred Users','Paid Appeals','Pending','Total Paid','Action'].map(h => (
                        <th key={h} className={`text-left text-xs font-medium p-4 ${textM}`}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {affiliates.length === 0 ? (
                        <tr><td colSpan={8} className={`p-12 text-center text-sm ${textM}`}>No affiliate partners yet. Assign "affiliate" role to users in the Users tab.</td></tr>
                      ) : affiliates.map(a => (
                        <tr key={a.id} className={`border-b ${borderCl} ${rowHover} transition-colors`}>
                          <td className={`p-4 text-sm ${textP}`}>{a.name}</td>
                          <td className={`p-4 text-sm ${textS}`}>{a.email}</td>
                          <td className={`p-4 text-xs font-mono ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{a.referral_code}</td>
                          <td className={`p-4 text-sm ${textP}`}>{a.referred_users}</td>
                          <td className={`p-4 text-sm ${textP}`}>{a.referred_appeals}</td>
                          <td className={`p-4 text-sm font-medium text-amber-500`}>${a.pending_earnings.toFixed(2)}</td>
                          <td className={`p-4 text-sm font-medium text-emerald-500`}>${a.total_paid.toFixed(2)}</td>
                          <td className="p-4">
                            <Button size="sm" onClick={() => handlePayout(a.id)} disabled={a.pending_earnings <= 0} className={`rounded-lg text-xs h-7 ${a.pending_earnings > 0 ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : isDark ? 'bg-white/5 text-white/30' : 'bg-gray-100 text-gray-400'}`}>
                              Mark Paid
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            {/* Contacts Tab */}
            <TabsContent value="contacts">
              <div className={`${card} rounded-2xl p-6`}>
                <div className="flex items-center gap-2 mb-6">
                  <MessageCircle className="w-5 h-5 text-blue-500" />
                  <h3 className={`text-lg font-semibold ${textP}`} style={{ fontFamily: 'Outfit' }}>Contact Messages</h3>
                </div>
                <div className="space-y-3">
                  {contacts.length === 0 ? (
                    <div className={`p-12 text-center ${textM}`}>No contact messages yet</div>
                  ) : contacts.map(c => (
                    <div key={c.id} className={`p-5 rounded-xl border ${isDark ? 'bg-white/[0.02] border-white/[0.05]' : 'bg-gray-50 border-gray-100'}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className={`font-semibold text-sm ${textP}`}>{c.name}</span>
                            <span className={`text-xs ${textM}`}>{c.email}</span>
                            <span className={`text-xs ${textM}`}>·</span>
                            <span className={`text-xs ${textM}`}>{new Date(c.created_at).toLocaleString()}</span>
                          </div>
                          <div className={`text-xs font-medium mb-2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{c.subject}</div>
                          <p className={`text-sm leading-relaxed ${textS}`}>{c.message}</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteContact(c.id)} className="text-red-500 hover:text-red-400 h-7 w-7 p-0 flex-shrink-0">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Settings Tab */}
            <TabsContent value="settings">
              <div className={`${card} rounded-2xl p-6`}>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <Settings className="w-5 h-5 text-blue-500" />
                    <h3 className={`text-lg font-semibold ${textP}`} style={{ fontFamily: 'Outfit' }}>System Settings</h3>
                  </div>
                  <Button onClick={handleSaveSettings} className="bg-blue-500 hover:bg-blue-600 text-white rounded-xl">
                    <Save className="w-4 h-4 mr-2" /> Save Settings
                  </Button>
                </div>
                {sysSettings ? (
                  <div className="space-y-8">
                    {/* General */}
                    <div>
                      <h4 className={`text-sm font-semibold mb-4 ${textS}`}>General Settings</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className={`text-xs block mb-1.5 ${textM}`}>Site Name</label>
                          <Input value={sysSettings.site_name} onChange={e => setSysSettings({...sysSettings, site_name: e.target.value})} className={`${inputCl} rounded-xl h-10`} />
                        </div>
                        <div>
                          <label className={`text-xs block mb-1.5 ${textM}`}>Support Email</label>
                          <Input value={sysSettings.support_email} onChange={e => setSysSettings({...sysSettings, support_email: e.target.value})} className={`${inputCl} rounded-xl h-10`} />
                        </div>
                        <div>
                          <label className={`text-xs block mb-1.5 ${textM}`}>Arbitration Email</label>
                          <Input value={sysSettings.arbitration_email} onChange={e => setSysSettings({...sysSettings, arbitration_email: e.target.value})} className={`${inputCl} rounded-xl h-10`} />
                        </div>
                        <div>
                          <label className={`text-xs block mb-1.5 ${textM}`}>Max Free Appeals</label>
                          <Input type="number" value={sysSettings.max_free_appeals} onChange={e => setSysSettings({...sysSettings, max_free_appeals: parseInt(e.target.value) || 1})} className={`${inputCl} rounded-xl h-10`} />
                        </div>
                      </div>
                    </div>
                    {/* Affiliate */}
                    <div>
                      <h4 className={`text-sm font-semibold mb-4 ${textS}`}>Affiliate Settings</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className={`text-xs block mb-1.5 ${textM}`}>Commission per Appeal ($)</label>
                          <Input type="number" step="0.01" value={sysSettings.affiliate_commission} onChange={e => setSysSettings({...sysSettings, affiliate_commission: parseFloat(e.target.value) || 0})} className={`${inputCl} rounded-xl h-10`} />
                        </div>
                        <div>
                          <label className={`text-xs block mb-1.5 ${textM}`}>Minimum Payout ($)</label>
                          <Input type="number" step="0.01" value={sysSettings.affiliate_min_payout} onChange={e => setSysSettings({...sysSettings, affiliate_min_payout: parseFloat(e.target.value) || 0})} className={`${inputCl} rounded-xl h-10`} />
                        </div>
                        <div>
                          <label className={`text-xs block mb-1.5 ${textM}`}>Affiliate Program Active</label>
                          <div className="flex items-center gap-3 h-10">
                            <button
                              onClick={() => setSysSettings({...sysSettings, affiliate_active: !sysSettings.affiliate_active})}
                              className={`relative w-12 h-6 rounded-full transition-colors ${sysSettings.affiliate_active ? 'bg-blue-500' : isDark ? 'bg-white/10' : 'bg-gray-200'}`}
                            >
                              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${sysSettings.affiliate_active ? 'translate-x-6' : 'translate-x-0.5'}`} />
                            </button>
                            <span className={`text-sm ${sysSettings.affiliate_active ? 'text-blue-500' : textM}`}>{sysSettings.affiliate_active ? 'Enabled' : 'Disabled'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={`p-12 text-center ${textM}`}>Loading settings...</div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
}

// =============== PLAN EDITOR ===============
function PlanEditor({ plan, onSave, onCancel, isDark }) {
  const [form, setForm] = useState(plan);
  const [featureInput, setFeatureInput] = useState('');
  const addFeature = () => { if (featureInput.trim()) { setForm({ ...form, features: [...(form.features || []), featureInput.trim()] }); setFeatureInput(''); } };
  const inputCl = isDark ? 'bg-white/[0.03] border-white/[0.06] text-white' : 'bg-gray-50 border-gray-200 text-gray-900';
  const dropCl = isDark ? 'bg-[#0A0A0C] border-white/10' : 'bg-white border-gray-200';
  const itemCl = isDark ? 'text-white hover:bg-white/5' : 'text-gray-700 hover:bg-gray-50';
  const textP = isDark ? 'text-white' : 'text-gray-900';
  const textM = isDark ? 'text-white/40' : 'text-gray-400';

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`mt-6 p-6 rounded-2xl space-y-4 border ${isDark ? 'bg-white/[0.03] border-white/[0.08]' : 'bg-gray-50 border-gray-200'}`} data-testid="plan-editor">
      <h4 className={`font-semibold ${textP}`} style={{ fontFamily: 'Outfit' }}>{plan.id ? 'Edit Plan' : 'New Plan'}</h4>
      <div className="grid grid-cols-2 gap-4">
        <div><label className={`text-xs block mb-1 ${textM}`}>Name (EN)</label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className={`${inputCl} rounded-lg h-10`} data-testid="plan-name-input" /></div>
        <div><label className={`text-xs block mb-1 ${textM}`}>Name (AR)</label><Input value={form.name_ar || ''} onChange={e => setForm({...form, name_ar: e.target.value})} className={`${inputCl} rounded-lg h-10`} /></div>
        <div><label className={`text-xs block mb-1 ${textM}`}>Price</label><Input type="number" step="0.01" value={form.price} onChange={e => setForm({...form, price: parseFloat(e.target.value) || 0})} className={`${inputCl} rounded-lg h-10`} data-testid="plan-price-input" /></div>
        <div><label className={`text-xs block mb-1 ${textM}`}>Currency</label>
          <Select value={form.currency} onValueChange={v => setForm({...form, currency: v})}><SelectTrigger className={`${inputCl} rounded-lg h-10`}><SelectValue /></SelectTrigger><SelectContent className={dropCl}><SelectItem value="eur" className={itemCl}>EUR</SelectItem><SelectItem value="usd" className={itemCl}>USD</SelectItem></SelectContent></Select>
        </div>
        <div><label className={`text-xs block mb-1 ${textM}`}>Billing Cycle</label>
          <Select value={form.billing_cycle} onValueChange={v => setForm({...form, billing_cycle: v})}><SelectTrigger className={`${inputCl} rounded-lg h-10`}><SelectValue /></SelectTrigger><SelectContent className={dropCl}><SelectItem value="one_time" className={itemCl}>One-time</SelectItem><SelectItem value="monthly" className={itemCl}>Monthly</SelectItem><SelectItem value="yearly" className={itemCl}>Yearly</SelectItem></SelectContent></Select>
        </div>
        <div><label className={`text-xs block mb-1 ${textM}`}>Max Appeals (-1 = unlimited)</label><Input type="number" value={form.max_appeals} onChange={e => setForm({...form, max_appeals: parseInt(e.target.value) || 0})} className={`${inputCl} rounded-lg h-10`} /></div>
      </div>
      <div><label className={`text-xs block mb-1 ${textM}`}>Description</label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} className={`${inputCl} rounded-lg h-10`} data-testid="plan-description-input" /></div>
      <div><label className={`text-xs block mb-1 ${textM}`}>Features</label>
        <div className="flex gap-2 mb-2"><Input value={featureInput} onChange={e => setFeatureInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && addFeature()} className={`${inputCl} rounded-lg h-10`} placeholder="Add feature..." /><Button onClick={addFeature} size="sm" className="bg-blue-500 hover:bg-blue-600 rounded-lg text-white"><Plus className="w-4 h-4" /></Button></div>
        <div className="flex flex-wrap gap-2">{(form.features || []).map((f, i) => (<span key={i} className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${isDark ? 'bg-white/[0.05] border border-white/[0.08] text-white/60' : 'bg-blue-50 border border-blue-100 text-blue-600'}`}>{f}<button onClick={() => setForm({...form, features: form.features.filter((_, idx) => idx !== i)})} className="text-red-500 ml-1">&times;</button></span>))}</div>
      </div>
      <div className="flex gap-2 pt-2">
        <Button onClick={() => onSave(form)} className="bg-blue-500 hover:bg-blue-600 rounded-xl text-white" data-testid="save-plan-btn"><Save className="w-4 h-4 mr-2" /> Save</Button>
        <Button variant="outline" onClick={onCancel} className={`rounded-xl ${isDark ? 'border-white/[0.08] text-white/60' : 'border-gray-200 text-gray-500'}`}>Cancel</Button>
      </div>
    </motion.div>
  );
}

// =============== SUGGESTIONS MANAGER ===============
function SuggestionsManager({ suggestions, onReload, isDark, card, textP, textS, textM, inputCl }) {
  const [newSug, setNewSug] = useState({ text: '', text_ar: '', text_de: '', platform: '', active: true, sort_order: 0 });
  const [editing, setEditing] = useState(null);

  const handleCreate = async () => {
    if (!newSug.text) return;
    try { await suggestionsAPI.create(newSug); toast.success('Created'); setNewSug({ text: '', text_ar: '', text_de: '', platform: '', active: true, sort_order: 0 }); onReload(); } catch { toast.error('Failed'); }
  };
  const handleUpdate = async (id, data) => { try { await suggestionsAPI.update(id, data); toast.success('Updated'); setEditing(null); onReload(); } catch { toast.error('Failed'); } };
  const handleDelete = async (id) => { try { await suggestionsAPI.delete(id); toast.success('Deleted'); onReload(); } catch { toast.error('Failed'); } };

  return (
    <div className={`${card} rounded-2xl p-6`} data-testid="suggestions-manager">
      <div className="flex items-center gap-2 mb-6">
        <Tag className="w-5 h-5 text-blue-500" />
        <h3 className={`text-lg font-semibold ${textP}`} style={{ fontFamily: 'Outfit' }}>Search Suggestions</h3>
      </div>

      {/* Add new */}
      <div className={`p-4 rounded-xl border mb-6 ${isDark ? 'bg-white/[0.02] border-white/[0.05]' : 'bg-gray-50 border-gray-200'}`}>
        <p className={`text-xs mb-3 ${textM}`}>Add New Suggestion</p>
        <div className="grid grid-cols-3 gap-3 mb-3">
          <Input value={newSug.text} onChange={e => setNewSug({...newSug, text: e.target.value})} className={`${inputCl} rounded-lg h-9`} placeholder="English text" data-testid="new-suggestion-en" />
          <Input value={newSug.text_ar} onChange={e => setNewSug({...newSug, text_ar: e.target.value})} className={`${inputCl} rounded-lg h-9`} placeholder="Arabic text" />
          <Input value={newSug.text_de} onChange={e => setNewSug({...newSug, text_de: e.target.value})} className={`${inputCl} rounded-lg h-9`} placeholder="German text" />
        </div>
        <Button onClick={handleCreate} size="sm" className="bg-blue-500 hover:bg-blue-600 rounded-lg text-white" data-testid="create-suggestion-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
      </div>

      {/* List */}
      <div className="space-y-2">
        {suggestions.map(s => (
          <div key={s.id} className={`flex items-center justify-between p-3 rounded-xl border ${isDark ? 'border-white/[0.04] hover:bg-white/[0.02]' : 'border-gray-100 hover:bg-gray-50'} transition-colors`} data-testid={`suggestion-item-${s.id}`}>
            {editing === s.id ? (
              <SuggestionEditRow s={s} isDark={isDark} inputCl={inputCl} onSave={(data) => handleUpdate(s.id, data)} onCancel={() => setEditing(null)} />
            ) : (
              <>
                <div className="flex-1">
                  <span className={`text-sm font-medium ${textP}`}>{s.text}</span>
                  {s.text_ar && <span className={`text-xs ml-2 ${textS}`}>| {s.text_ar}</span>}
                  {s.text_de && <span className={`text-xs ml-2 ${textS}`}>| {s.text_de}</span>}
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(s.id)} className="text-blue-500 h-7 w-7 p-0"><Edit2 className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(s.id)} className="text-red-500 h-7 w-7 p-0"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function SuggestionEditRow({ s, isDark, inputCl, onSave, onCancel }) {
  const [form, setForm] = useState({ text: s.text, text_ar: s.text_ar || '', text_de: s.text_de || '' });
  return (
    <div className="flex items-center gap-2 w-full">
      <Input value={form.text} onChange={e => setForm({...form, text: e.target.value})} className={`${inputCl} rounded-lg h-8 flex-1`} />
      <Input value={form.text_ar} onChange={e => setForm({...form, text_ar: e.target.value})} className={`${inputCl} rounded-lg h-8 w-28`} placeholder="AR" />
      <Input value={form.text_de} onChange={e => setForm({...form, text_de: e.target.value})} className={`${inputCl} rounded-lg h-8 w-28`} placeholder="DE" />
      <Button size="sm" onClick={() => onSave(form)} className="bg-blue-500 text-white h-8 px-2"><Save className="w-3 h-3" /></Button>
      <Button size="sm" variant="ghost" onClick={onCancel} className="h-8 px-2 text-red-500">&times;</Button>
    </div>
  );
}

// =============== FIELDS MANAGER ===============
function FieldsManager({ fields, onReload, isDark, card, textP, textS, textM, inputCl, dropCl, itemCl }) {
  const [platform, setPlatform] = useState('');
  const [newField, setNewField] = useState({ platform: '', field_name: '', field_label: '', field_label_ar: '', field_label_de: '', field_type: 'text', required: false, options: [], sort_order: 0 });
  const [optInput, setOptInput] = useState('');

  const filteredFields = platform ? fields.filter(f => f.platform === platform) : fields;
  const platforms = [...new Set(fields.map(f => f.platform))].filter(Boolean);

  const handleCreate = async () => {
    if (!newField.platform || !newField.field_name || !newField.field_label) return;
    try { await platformFieldsAPI.create(newField); toast.success('Field created'); setNewField({ platform: '', field_name: '', field_label: '', field_label_ar: '', field_label_de: '', field_type: 'text', required: false, options: [], sort_order: 0 }); onReload(); } catch { toast.error('Failed'); }
  };
  const handleDelete = async (id) => { try { await platformFieldsAPI.delete(id); toast.success('Deleted'); onReload(); } catch { toast.error('Failed'); } };

  return (
    <div className={`${card} rounded-2xl p-6`} data-testid="fields-manager">
      <div className="flex items-center gap-2 mb-6">
        <Columns className="w-5 h-5 text-blue-500" />
        <h3 className={`text-lg font-semibold ${textP}`} style={{ fontFamily: 'Outfit' }}>Dynamic Form Fields</h3>
      </div>
      <p className={`text-sm mb-4 ${textS}`}>Add custom fields per platform. These appear in the appeal submission form when the user selects a specific platform.</p>

      {/* Add new */}
      <div className={`p-4 rounded-xl border mb-6 ${isDark ? 'bg-white/[0.02] border-white/[0.05]' : 'bg-gray-50 border-gray-200'}`}>
        <p className={`text-xs mb-3 ${textM}`}>Add New Field</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
          <Input value={newField.platform} onChange={e => setNewField({...newField, platform: e.target.value})} className={`${inputCl} rounded-lg h-9`} placeholder="Platform (e.g. Facebook)" data-testid="new-field-platform" />
          <Input value={newField.field_name} onChange={e => setNewField({...newField, field_name: e.target.value})} className={`${inputCl} rounded-lg h-9`} placeholder="field_name (e.g. username)" data-testid="new-field-name" />
          <Input value={newField.field_label} onChange={e => setNewField({...newField, field_label: e.target.value})} className={`${inputCl} rounded-lg h-9`} placeholder="Label (e.g. Username)" data-testid="new-field-label" />
          <Input value={newField.field_label_ar} onChange={e => setNewField({...newField, field_label_ar: e.target.value})} className={`${inputCl} rounded-lg h-9`} placeholder="Label AR" />
          <Select value={newField.field_type} onValueChange={v => setNewField({...newField, field_type: v})}>
            <SelectTrigger className={`${inputCl} rounded-lg h-9`}><SelectValue /></SelectTrigger>
            <SelectContent className={dropCl}>
              {['text','textarea','select','email','url','number'].map(t => (<SelectItem key={t} value={t} className={itemCl}>{t}</SelectItem>))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={newField.required} onChange={e => setNewField({...newField, required: e.target.checked})} className="rounded" />
            <label className={`text-xs ${textS}`}>Required</label>
          </div>
        </div>
        {newField.field_type === 'select' && (
          <div className="mb-3">
            <div className="flex gap-2 mb-2">
              <Input value={optInput} onChange={e => setOptInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && optInput.trim()) { setNewField({...newField, options: [...newField.options, optInput.trim()]}); setOptInput(''); }}} className={`${inputCl} rounded-lg h-8`} placeholder="Add option..." />
            </div>
            <div className="flex flex-wrap gap-1">{newField.options.map((o, i) => (<span key={i} className={`text-xs px-2 py-1 rounded-full ${isDark ? 'bg-white/5 text-white/60' : 'bg-blue-50 text-blue-600'}`}>{o} <button onClick={() => setNewField({...newField, options: newField.options.filter((_,idx) => idx !== i)})} className="text-red-500 ml-1">&times;</button></span>))}</div>
          </div>
        )}
        <Button onClick={handleCreate} size="sm" className="bg-blue-500 hover:bg-blue-600 rounded-lg text-white" data-testid="create-field-btn"><Plus className="w-4 h-4 mr-1" /> Add Field</Button>
      </div>

      {/* Filter */}
      {platforms.length > 0 && (
        <div className="flex gap-2 mb-4 flex-wrap">
          <Button variant={!platform ? 'default' : 'outline'} size="sm" onClick={() => setPlatform('')} className={`rounded-lg ${!platform ? 'bg-blue-500 text-white' : isDark ? 'border-white/[0.08] text-white/60' : 'border-gray-200 text-gray-500'}`}>All</Button>
          {platforms.map(p => (
            <Button key={p} variant={platform === p ? 'default' : 'outline'} size="sm" onClick={() => setPlatform(p)} className={`rounded-lg ${platform === p ? 'bg-blue-500 text-white' : isDark ? 'border-white/[0.08] text-white/60' : 'border-gray-200 text-gray-500'}`}>{p}</Button>
          ))}
        </div>
      )}

      {/* Fields list */}
      <div className="space-y-2">
        {filteredFields.map(f => (
          <div key={f.id} className={`flex items-center justify-between p-3 rounded-xl border ${isDark ? 'border-white/[0.04]' : 'border-gray-100'}`} data-testid={`field-item-${f.id}`}>
            <div>
              <div className="flex items-center gap-2">
                <Badge className={isDark ? 'bg-blue-500/10 text-blue-400' : 'bg-blue-50 text-blue-600'}>{f.platform}</Badge>
                <span className={`text-sm font-medium ${textP}`}>{f.field_label}</span>
                <span className={`text-xs ${textM}`}>({f.field_type})</span>
                {f.required && <Badge className={isDark ? 'bg-red-500/10 text-red-400' : 'bg-red-50 text-red-600'}>Required</Badge>}
              </div>
              <p className={`text-xs mt-1 ${textM}`}>Field: {f.field_name}{f.field_label_ar ? ` | AR: ${f.field_label_ar}` : ''}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => handleDelete(f.id)} className="text-red-500 h-7 w-7 p-0"><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
        ))}
        {filteredFields.length === 0 && <p className={`text-center py-4 ${textM}`}>No custom fields defined yet</p>}
      </div>
    </div>
  );
}
